import React, { useState } from 'react';
import clsx from 'clsx';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import {
  Box,
  Grid,
  Button,
  FormHelperText,
  TextField,
  makeStyles,
  Typography,
  CircularProgress
} from '@material-ui/core';
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import FirebaseError from '../../../utils/firebaseErrorMsg';

const useStyles = makeStyles((theme) => ({
  root: {},
  googleButton: {
    backgroundColor: theme.palette.common.white
  },
  providerIcon: {
    marginRight: theme.spacing(2)
  },
  divider: {
    flexGrow: 1
  },
  dividerText: {
    margin: theme.spacing(2)
  },
  loginSubOptions: {
    paddingTop: theme.spacing(2)
  },
  textRight: {
    textAlign: 'right'
  },
  textLeft: {
    textAlign: 'left'
  },
  currentMethodIcon: {
    height: 30,
    margin: theme.spacing(2),
    '& > img': {
      width: 'auto',
      maxHeight: '100%'
    }
  }
}));

const LoginAuthView = ({ className, loading }) => {
  const classes = useStyles();
  const { signInWithEmailAndPassword, sendPasswordResetEmail } = useAuth();
  const isMountedRef = useIsMountedRef();
  const [resetPass, setResetPass] = useState(false);
  const [activateLogin, setActivateLogin] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleResetPass = () => {
    setResetPass(!resetPass);
    setActivateLogin(false);
  };
  const handleActivateLogin = () => {
    setActivateLogin(!activateLogin);
    setResetPass(false);
  };
  return (
    <>
      <Box alignItems="center" display="flex">
        <Typography color="textPrimary" variant="body1">
          {!resetPass && !activateLogin && 'Faça login para continuar'}
          {resetPass && !activateLogin && 'Digite abaixo seu email para que possamos enviar um link para alteração da senha'}
          {!resetPass && activateLogin && 'Digite abaixo seu email cadastrado para seguir com o processo de ativação da sua conta'}
        </Typography>
      </Box>
      {resetPass || activateLogin ? (
        <Formik
          initialValues={{
            email: '',
            submit: null
          }}
          validationSchema={Yup.object().shape({
            email: Yup.string()
              .email('Deve ser um email válido')
              .max(255)
              .required('Email é obrigatório')
          })}
          onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
            try {
              await sendPasswordResetEmail(values.email);

              if (isMountedRef.current) {
                setStatus({ success: true });
                setSubmitting(false);
                enqueueSnackbar(
                  'Um novo email foi enviado para o seu endereço para definir a senha',
                  {
                    variant: 'success'
                  }
                );
              }
            } catch (err) {
              const errorMsgPTBR = FirebaseError(err);
              if (isMountedRef.current) {
                setStatus({ success: false });
                setErrors({ submit: errorMsgPTBR });
                setSubmitting(false);
                enqueueSnackbar('Erro ao enviar email', {
                  variant: 'error'
                });
              }
            }
          }}>
          {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
            <form noValidate onSubmit={handleSubmit} className={clsx(classes.root, className)}>
              <TextField
                error={Boolean(touched.email && errors.email)}
                fullWidth
                helperText={touched.email && errors.email}
                label="Email"
                margin="normal"
                name="email"
                onBlur={handleBlur}
                onChange={handleChange}
                type="email"
                value={values.email}
                variant="outlined"
              />
              {errors.submit && (
                <Box mt={3}>
                  <FormHelperText error>{errors.submit}</FormHelperText>
                </Box>
              )}
              <Box mt={2}>
                <Button
                  color="secondary"
                  disabled={isSubmitting}
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                >
                  {activateLogin ? 'Ativar Minha Conta' : 'Enviar Link'}
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      ) : (
        <Formik
          initialValues={{
            email: '',
            password: '',
            submit: null
          }}
          validationSchema={Yup.object().shape({
            email: Yup.string()
              .email('Deve ser um email válido')
              .max(255)
              .required('Email é obrigatório'),
            password: Yup.string().max(255).required('Senha é obrigatória')
          })}
          onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
            try {
              await signInWithEmailAndPassword(values.email, values.password);
              if (isMountedRef.current) {
                setStatus({ success: true });
                setSubmitting(false);
              }
            } catch (err) {
              const errorMsgPTBR = FirebaseError(err);
              if (isMountedRef.current) {
                setStatus({ success: false });
                setErrors({ submit: errorMsgPTBR });
                setSubmitting(false);
              }
            }
          }}>
          {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
            <form noValidate onSubmit={handleSubmit} className={clsx(classes.root, className)}>
              <TextField
                error={Boolean(touched.email && errors.email)}
                fullWidth
                helperText={touched.email && errors.email}
                label="Email"
                margin="normal"
                name="email"
                onBlur={handleBlur}
                onChange={handleChange}
                type="email"
                value={values.email}
                variant="outlined"
              />
              <TextField
                error={Boolean(touched.password && errors.password)}
                fullWidth
                helperText={touched.password && errors.password}
                label="Senha"
                margin="normal"
                name="password"
                onBlur={handleBlur}
                onChange={handleChange}
                type="password"
                value={values.password}
                variant="outlined"
              />
              {errors.submit && (
                <Box mt={3}>
                  <FormHelperText error>{errors.submit}</FormHelperText>
                </Box>
              )}
              <Box mt={2}>
                <Button
                  color="secondary"
                  disabled={isSubmitting || loading}
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                >
                  {loading ? <CircularProgress size={24} /> : "Entrar"}
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      )}
      <Grid
        container
        className={classes.loginSubOptions}
        spacing={1}
        alignItems="center"
      >
        <Grid item  className={classes.textLeft} xs={6}>
          {!activateLogin && (
            <Button
              color="secondary"
              size="medium"
              onClick={handleResetPass}
            >
              {resetPass ? 'Voltar ao Login' : 'Esqueci minha Senha'}
            </Button>
          )}
        </Grid>
        {/* <Grid item justify="center" xs={6}>
          {!resetPass && (
            <Button
              color="secondary"
              size="medium"
              onClick={handleActivateLogin}
            >
              {activateLogin ? 'Voltar ao Login' : 'Ativar minha Conta'}
            </Button>
          )}
        </Grid> */}
      </Grid>
    </>
  );
};

LoginAuthView.propTypes = {
  className: PropTypes.string,
  loading: PropTypes.bool
};

export default LoginAuthView;